/**
 * Strategy pattern service for importing multi-tab workbooks. The main workflow is:
 * 1) getMatchedSheets() - read the sheet names and match them to importers, returning a list of MatchedSheetEntry (sheet + owning importer) and skipped sheet names
 * 2) parseAll() - call each matched sheet's importer to parse the file and return a SheetParseResult (valid row count, errors, and opaque parsed data)
 * 3) importAll() - call each matched sheet's importer to import the parsed data, returning an ImportResult (created/updated counts and errors)
 *
 * This service doesn't know anything about the specifics of any particular importer or sheet type; it just orchestrates the workflow. All sheet-specific logic is contained in the SheetImporter implementations.
 */
import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FileImportService } from '../../shared/services/file-import/file-import.service';
import {
  ImportOptions,
  ImportResult,
  MatchedSheet,
  SheetImporter,
  SheetParseResult,
} from './schemas/sheet-importer.interface';

/** A matched sheet paired with the importer that owns it. Kept together so callers
 *  never have to maintain two parallel arrays indexed in sync.
 * ex: for timeseries, there would be a matched sheet for each timeseries table, all owned by the TimeseriesSheetImporterService as SheetImporter
 * */
export interface MatchedSheetEntry {
  match: MatchedSheet;
  importer: SheetImporter;
}

export interface GetMatchedSheetsResult {
  matched: MatchedSheetEntry[];
  skipped: string[];
}

@Injectable({ providedIn: 'root' })
export class MultiTabImportService {
  private readonly fileImport = inject(FileImportService);

  private readonly importCompletedSubject = new Subject<void>();
  /** Emits after a successful multi-tab import. Subscribe to trigger data refreshes in affected pages. */
  readonly importCompleted$: Observable<void> = this.importCompletedSubject.asObservable();

  notifyImportCompleted(): void {
    this.importCompletedSubject.next();
  }

  /** Read the workbook's sheet names and match each one against the registered importers.
   *  First importer to claim a sheet name wins; unmatched names go into `skipped`. */
  async getMatchedSheets(
    file: File,
    importers: SheetImporter[],
  ): Promise<GetMatchedSheetsResult> {
    const sheetNames = await this.fileImport.getSheetNames(file);
    const matched: MatchedSheetEntry[] = [];
    const skipped: string[] = [];

    for (let i = 0; i < sheetNames.length; i++) {
      const name = sheetNames[i];
      let matchFound = false;

      // maybe if this is inneficient in future we can use promise.all to check all importers in parallel,
      // but for now this is simpler
      for (const importer of importers) {
        const match = importer.matchSheet(name, i);
        if (match !== null) {
          matched.push({ match, importer });
          matchFound = true;
          break;
        }
      }

      if (!matchFound) {
        skipped.push(name);
      }
    }

    return { matched, skipped };
  }

  /** Parse all matched sheets in parallel — safe since parsing is read-only. */
  async parseAll(
    file: File,
    matched: MatchedSheetEntry[],
  ): Promise<SheetParseResult[]> {
    return Promise.all(
      matched.map(({ match, importer }) =>
        // Separator is irrelevant for XLSX but required by the interface for future CSV importers
        importer.parseSheet(file, match, ','),
      ),
    );
  }

  /** Import all matched sheets sequentially to avoid overwhelming the backend. automatically calls the correct importer service*/
  async importAll(
    matched: MatchedSheetEntry[],
    parseResults: SheetParseResult[],
    options: ImportOptions,
  ): Promise<ImportResult[]> {
    const results: ImportResult[] = [];
    for (let i = 0; i < matched.length; i++) {
      try {
        const result = await matched[i].importer.importRecords(
          matched[i].match,
          parseResults[i],
          options,
        );
        results.push(result);
      } catch (err) {
        // One sheet failing must not abort the remaining sheets.
        results.push({
          created: 0,
          updated: 0,
          errors: [],
          sheetError: err instanceof Error ? err.message : 'Import failed',
        });
      }
    }
    return results;
  }
}
